import * as Sentry from '@sentry/vue'

type NetworkConnectionSnapshot = {
  effectiveType?: string
  downlink?: number
  rtt?: number
  saveData?: boolean
  type?: string
}

type ChapterSaveSentryCaptureOptions = {
  handled?: boolean
}

type ChapterSaveSentryContextInput = {
  error: unknown
  chapterId: string
  projectType?: string
  params: {
    title?: string
    jsonContent?: object
    force?: boolean
    timeZone?: string
  }
  options?: {
    force?: boolean
    projectType?: string
    skipDeletionCheck?: boolean
  }
  savingStateAtStart: string
  chapterStillCurrent: boolean
  hasLocalBackup: boolean
  hasPendingMassDeletion: boolean
  pendingLegacyBackupSync: boolean
  inFlightSaves: number
  attemptDurationMs: number
}

let pageUnloadListenersInstalled = false
let pageUnloading = false

const reportedChapterSaveAuthDenials = new Set<string>()
const reportedChapterSaveDiagnostics = new Set<string>()

export function clearChapterSaveAuthDenialReports(chapterId: string) {
  reportedChapterSaveAuthDenials.delete(chapterId)
}

export function clearChapterSaveDiagnosticReports(chapterId: string): void {
  reportedChapterSaveDiagnostics.delete(chapterId)
}

export function shouldCaptureChapterSaveDiagnostic(chapterId: string): boolean {
  if (reportedChapterSaveDiagnostics.has(chapterId)) {
    return false
  }
  reportedChapterSaveDiagnostics.add(chapterId)
  return true
}

export function shouldCaptureChapterSaveHttpError(status: number | undefined): boolean {
  if (status === undefined) return true
  if (status >= 500) return true
  return status === 401 || status === 403
}

/**
 * For 401/403, report at most once per chapter until the next successful save.
 * Other statuses are always eligible for capture.
 */
export function shouldCaptureChapterSaveAttempt(
  chapterId: string,
  status: number | undefined,
): boolean {
  if (status !== 401 && status !== 403) return true
  if (reportedChapterSaveAuthDenials.has(chapterId)) return false
  reportedChapterSaveAuthDenials.add(chapterId)
  return true
}

function ensurePageUnloadListeners() {
  if (pageUnloadListenersInstalled || typeof window === 'undefined') return
  pageUnloadListenersInstalled = true

  const markUnloading = () => {
    pageUnloading = true
  }

  window.addEventListener('pagehide', markUnloading)
  window.addEventListener('beforeunload', markUnloading)
}

function getNetworkConnectionSnapshot(): NetworkConnectionSnapshot | undefined {
  const nav = navigator as Navigator & {
    connection?: NetworkConnectionSnapshot
    mozConnection?: NetworkConnectionSnapshot
    webkitConnection?: NetworkConnectionSnapshot
  }
  const connection = nav.connection || nav.mozConnection || nav.webkitConnection
  if (!connection) return undefined

  return {
    effectiveType: connection.effectiveType,
    downlink: connection.downlink,
    rtt: connection.rtt,
    saveData: connection.saveData,
    type: connection.type,
  }
}

function sanitizeApiUrl(url?: string): string | undefined {
  if (!url) return undefined
  return url.replace(/\/[a-f0-9]{24}(?=\/|$|\?)/gi, '/:id')
}

function estimateJsonBytes(value: unknown): number | undefined {
  try {
    return new TextEncoder().encode(JSON.stringify(value)).length
  } catch {
    return undefined
  }
}

function classifyChapterSaveError(error: unknown): string {
  const axiosError = error as {
    code?: string
    message?: string
    response?: { status?: number }
  } | null

  const status = axiosError?.response?.status
  if (typeof status === 'number') {
    if (status >= 500) return 'http_5xx'
    if (status >= 400) return 'http_4xx'
    return `http_${status}`
  }

  const code = axiosError?.code
  if (code === 'ERR_NETWORK') return 'network'
  if (code === 'ECONNABORTED') return 'timeout'
  if (code === 'ERR_CANCELED') return 'canceled'
  if (/network error/i.test(String(axiosError?.message || ''))) return 'network'
  if (/timeout/i.test(String(axiosError?.message || ''))) return 'timeout'
  if (!axiosError?.response) return 'no_response'
  return 'unknown'
}

function payloadSizeBucket(bytes: number | undefined): string {
  if (bytes === undefined) return 'unknown'
  if (bytes < 10_000) return 'lt_10kb'
  if (bytes < 50_000) return '10_50kb'
  if (bytes < 200_000) return '50_200kb'
  if (bytes < 1_000_000) return '200kb_1mb'
  return 'gte_1mb'
}

export type ChapterSaveDiagnosticReason =
  | 'patch_timeout_online'
  | 'lock_busy'
  | 'load_chapter_conflict'
  | 'content_hash_mismatch_no_backup'

type ChapterSaveDiagnosticInput = {
  chapterId: string
  projectId?: string
  attemptDurationMs?: number
  inFlightSaves?: number
  navigatorOnLine?: boolean
}

/** Warning-level telemetry without chapter body content. */
export function captureChapterSaveDiagnostic(
  reason: ChapterSaveDiagnosticReason,
  input: ChapterSaveDiagnosticInput,
): void {
  ensurePageUnloadListeners()

  if (reason !== 'load_chapter_conflict' && !shouldCaptureChapterSaveDiagnostic(input.chapterId)) {
    return
  }

  Sentry.withScope((scope) => {
    scope.setLevel('warning')
    scope.setTag('feature', 'chapter-save')
    scope.setTag('chapter_save.diagnostic', reason)
    scope.setTag('navigator.onLine', String(input.navigatorOnLine ?? navigator.onLine))
    scope.setContext('chapter_save_diagnostic', {
      chapterId: input.chapterId,
      projectId: input.projectId,
      attemptDurationMs: input.attemptDurationMs,
      inFlightSaves: input.inFlightSaves,
    })
    Sentry.captureMessage(`chapter-save:${reason}`, 'warning')
  })
}

/**
 * Captures chapter save failures with tags/contexts useful for Sentry Discover.
 * Avoids sending chapter body content.
 */
export function captureChapterSaveException(
  input: ChapterSaveSentryContextInput,
  options?: ChapterSaveSentryCaptureOptions,
) {
  ensurePageUnloadListeners()

  const axiosError = input.error as {
    code?: string
    message?: string
    name?: string
    response?: { status?: number; statusText?: string }
    config?: {
      method?: string
      url?: string
      baseURL?: string
      timeout?: number
      headers?: Record<string, unknown>
    }
  }

  const network = getNetworkConnectionSnapshot()
  const paramKeys = Object.keys(input.params)
  const payloadBytes = estimateJsonBytes(input.params)
  const errorKind = classifyChapterSaveError(input.error)
  const httpStatus = axiosError?.response?.status
  const axiosCode = axiosError?.code || 'none'
  const visibilityState =
    typeof document !== 'undefined' ? document.visibilityState : 'unknown'
  const documentHidden = typeof document !== 'undefined' ? document.hidden : undefined

  Sentry.withScope((scope) => {
    scope.setTag('feature', 'chapter-save')
    scope.setTag('error_kind', errorKind)
    scope.setTag('axios.code', axiosCode)
    scope.setTag('navigator.onLine', String(navigator.onLine))
    scope.setTag('document.visibilityState', visibilityState)
    scope.setTag('page.unloading', String(pageUnloading))
    scope.setTag('chapter.still_current', String(input.chapterStillCurrent))
    scope.setTag('save.has_json_content', String(paramKeys.includes('jsonContent')))
    scope.setTag('save.has_title', String(paramKeys.includes('title')))
    scope.setTag('save.force', String(Boolean(input.params.force || input.options?.force)))
    scope.setTag('save.payload_size_bucket', payloadSizeBucket(payloadBytes))
    scope.setTag('save.in_flight', String(input.inFlightSaves))

    if (input.projectType) {
      scope.setTag('project.type', String(input.projectType))
    }
    if (network?.effectiveType) {
      scope.setTag('connection.effectiveType', network.effectiveType)
    }
    if (typeof httpStatus === 'number') {
      scope.setTag('http.status_code', String(httpStatus))
    }

    scope.setContext('chapter_save', {
      chapterId: input.chapterId,
      projectType: input.projectType,
      paramKeys,
      payloadBytes,
      force: Boolean(input.params.force || input.options?.force),
      skipDeletionCheck: Boolean(input.options?.skipDeletionCheck),
      timeZone: input.params.timeZone,
      savingStateAtStart: input.savingStateAtStart,
      chapterStillCurrent: input.chapterStillCurrent,
      hasLocalBackup: input.hasLocalBackup,
      hasPendingMassDeletion: input.hasPendingMassDeletion,
      pendingLegacyBackupSync: input.pendingLegacyBackupSync,
      inFlightSaves: input.inFlightSaves,
      attemptDurationMs: input.attemptDurationMs,
    })

    scope.setContext('axios', {
      name: axiosError?.name,
      message: axiosError?.message,
      code: axiosError?.code,
      method: axiosError?.config?.method
        ? String(axiosError.config.method).toUpperCase()
        : undefined,
      url: sanitizeApiUrl(axiosError?.config?.url),
      baseURL: axiosError?.config?.baseURL,
      timeout: axiosError?.config?.timeout,
      status: httpStatus,
      statusText: axiosError?.response?.statusText,
      hasResponse: Boolean(axiosError?.response),
    })

    scope.setContext('network', {
      onLine: navigator.onLine,
      ...network,
    })

    scope.setContext('page', {
      visibilityState,
      hidden: documentHidden,
      unloading: pageUnloading,
      pathname: typeof window !== 'undefined' ? window.location.pathname : undefined,
      href: typeof window !== 'undefined' ? window.location.href : undefined,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      language: typeof navigator !== 'undefined' ? navigator.language : undefined,
    })

    scope.setExtra('save.paramKeys', paramKeys)
    scope.setExtra('save.payloadBytes', payloadBytes)
    scope.setExtra('save.attemptDurationMs', input.attemptDurationMs)

    if (options?.handled) {
      scope.setLevel('warning')
    }

    Sentry.captureException(
      input.error,
      options?.handled
        ? {
            mechanism: {
              handled: true,
              type: 'chapter-save',
            },
          }
        : undefined,
    )
  })
}

// Install early so pagehide/beforeunload are observed before a failing save.
ensurePageUnloadListeners()
